import { all, select, put, call, race, take } from 'redux-saga/effects';
import i18n from 'i18next';
import { concurrentHandler, bufferedHandler, callback, evaluate } from '../../tools/effects';
import { generateLayer, createThumbnail, createThumbnailCSqueeze } from '../../../algorithms/imagery';
import { generateVisualizationParams } from '../../../algorithms/utils';
import { aggregateMissionsDates } from '../../../common/algorithms';
import { Types, Actions } from './header';
import { Actions as Imagery } from '../imagery';
import { getAcquisitionParameters, getImageryIdentifiers } from '../../../selectors';
import { ee } from '../../../services/earth-engine';

// startDate/endDate (optional) restrict the search to that period instead of
// the mission's entire history; omit both to search everything (e.g. YearsSelect,
// which needs the full available history to let the user pick any past year).
const handleLoadAvailableImages = function* ({ payload }) {
  const { startDate, endDate } = payload || {};
  const { satellite, geometry } = yield select(getAcquisitionParameters);
  const type = window.sessionStorage.getItem('typeSatellite');
  const missions = type === null || type === 'TOA' ? satellite.missions : satellite.surfaceReflectance;
  const results = yield all(missions.map((mission) => call(fetchMissionData, mission, geometry, startDate, endDate)));
  const availableByMission = results.filter(Boolean);
  const availableDates = aggregateMissionsDates(availableByMission);
  if (availableDates !== undefined) yield put(Actions.setAvailableDates(availableDates, availableByMission));
};

const toDateOnly = (date) => new Date(date).toISOString().slice(0, 10);

// period.start/period.end (and similar upstream dates) can carry a
// sub-day/local-timezone time-of-day component instead of landing exactly on
// UTC midnight. Rounding to UTC midnight up front means every comparison and
// the final toDateOnly() string always agree — no more two Date objects that
// are hours apart (and so compare as non-degenerate) silently truncating to
// the same calendar-day string once sent to Earth Engine as YYYY-MM-DD.
const toUtcMidnight = (date) => new Date(`${toDateOnly(date)}T00:00:00.000Z`);

/**
 * @constant MS_PER_DAY
 * @description One day in milliseconds. Safe to add to a UTC midnight, which is what every date
 * here has been rounded to: UTC has no daylight saving, so a day is always this long.
 * @memberof module:Shared
 */
const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * @function nextDay
 * @description The day after the one given, for turning the inclusive end of a period into the
 * exclusive end Earth Engine expects.
 * `filterDate` leaves its end instant out, while the period a user picks takes its end day in, so
 * a range that ended at that day's midnight never searched the day itself. Nothing captured on the
 * last day of a period was ever returned - and since the newest image of a selection is what sets
 * that end date, running the same search again came back exactly one image short of what was
 * already on screen. The same gap swallowed every 31 December, where one year's chunk ended on the
 * instant the next one started, and it collapsed a single-day search into an empty range Earth
 * Engine refuses outright.
 * @param {Date} date - UTC midnight of the last day to include.
 * **Called by:
 * @see {buildYearlyChunks}
 * @memberof module:Shared
 * @returns {Date} UTC midnight of the following day.
 */
const nextDay = (date) => new Date(date.getTime() + MS_PER_DAY);

// Splits the requested range into one-year chunks (oldest first, so the merged
// dictionary keys stay in chronological order), so each Earth Engine request
// only needs to walk a year of revisit cycles instead of the mission's entire
// history (which can span a decade or more).
const buildYearlyChunks = (rangeStart, rangeEnd) => {
  const start = toUtcMidnight(rangeStart);
  const end = toUtcMidnight(rangeEnd);
  const chunks = [];
  for (let year = start.getUTCFullYear(); year <= end.getUTCFullYear(); year++) {
    const yearStart = new Date(Date.UTC(year, 0, 1));
    const yearEnd = new Date(Date.UTC(year, 11, 31));
    const chunkStart = yearStart > start ? yearStart : start;
    const chunkEnd = yearEnd < end ? yearEnd : end;
    /**
     * Only an inverted range is dropped: a chunk whose start and end land on the same day is a
     * legitimate one-day search, and the exclusive end below turns it into a whole day rather than
     * the empty instant Earth Engine rejects ("Empty date ranges not supported").
     * @see {@link nextDay}
     */
    if (chunkStart > chunkEnd) continue;
    chunks.push({ start: toDateOnly(chunkStart), end: toDateOnly(nextDay(chunkEnd)) });
  }
  return chunks;
};

// Two adjacent year-chunks each independently phase-align their own cycle
// windows (see processCollection), so a real image sitting near a chunk
// boundary can occasionally surface twice, a few seconds/minutes apart.
// Collapse near-duplicates after merging instead of risking subtle date-math
// changes on the Earth Engine side.
const DEDUPE_WINDOW_MS = 5 * 60 * 1000;
const dedupeCloseDates = (dict) => {
  const sortedKeys = Object.keys(dict).sort((a, b) => new Date(a) - new Date(b));
  const result = {};
  let lastTime = null;
  for (const key of sortedKeys) {
    const time = new Date(key).getTime();
    if (lastTime !== null && time - lastTime < DEDUPE_WINDOW_MS) continue;
    result[key] = dict[key];
    lastTime = time;
  }
  return result;
};

// Isolates a single chunk's size check so one bad chunk can't fail the whole
// yield all() (Promise.all semantics: one rejection kills every other chunk's
// result too). Logs the exact mission/date-range on failure so a persistent
// server-side error can actually be diagnosed instead of just retried blind.
function* fetchChunkSize(mission, geometry, chunk) {
  try {
    return yield call(
      evaluate,
      ee.ImageCollection(mission.name).filterBounds(geometry).filterDate(chunk.start, chunk.end).size(),
    );
  } catch (e) {
    console.error('[fetchAllDates] size check failed', mission.name, chunk, e);
    return 0;
  }
}

// Same isolation for the actual queryAvailable call: if a specific chunk's
// windowing logic errors server-side, that chunk just contributes no dates
// instead of failing the entire mission's search.
function* fetchChunkDates(mission, geometry, chunk) {
  try {
    return yield call(evaluate, mission.algorithms.queryAvailable(geometry, chunk.start, chunk.end));
  } catch (e) {
    console.error('[fetchAllDates] queryAvailable failed', mission.name, chunk, e);
    return null;
  }
}

function* fetchAllDates(mission, geometry, startDate, endDate) {
  const rangeStart = startDate || `${mission.startYear}-01-01`;
  const rangeEnd = endDate || (mission.endYear ? `${mission.endYear}-12-31` : new Date().toISOString());
  const chunks = buildYearlyChunks(rangeStart, rangeEnd);

  const sizes = yield all(chunks.map((chunk) => call(fetchChunkSize, mission, geometry, chunk)));
  const nonEmptyChunks = chunks.filter((_, i) => sizes[i] > 0);

  const chunkResults = yield all(nonEmptyChunks.map((chunk) => call(fetchChunkDates, mission, geometry, chunk)));
  const merged = Object.assign({}, ...chunkResults.filter(Boolean));
  return dedupeCloseDates(merged);
}

function* fetchMissionData(mission, geometry, startDate, endDate) {
  try {
    let dates = yield call(fetchAllDates, mission, geometry, startDate, endDate);
    if (Object.keys(dates).length === 0 && mission.fallbackMission) {
      yield put(Actions.setMissionFallback(mission));
      const fallback = mission.fallbackMission;
      dates = yield call(fetchAllDates, fallback, geometry, startDate, endDate);
      return { name: fallback.name, shortname: fallback.shortname, dates };
    }
    return { name: mission.name, shortname: mission.shortname, dates };
  } catch (e) {
    console.error('Error fetching mission', mission.name, e);
    return null;
  }
}

const handleAcquireImage = function* ({ payload: { missionName, date } }) {
  const { geometry, satellite } = yield select(getAcquisitionParameters);
  const { imageId } = yield select(getImageryIdentifiers);
  const mission = satellite.get(missionName);
  let dtStart = new Date(date);
  dtStart.setUTCHours(0, 0, 0, 0);
  let dtEnd = new Date(date);
  dtEnd.setUTCHours(23, 59, 59, 999);
  const collection = ee
    .ImageCollection(missionName)
    .filterBounds(geometry)
    .filter(ee.Filter.date(dtStart.toISOString(), dtEnd.toISOString()));
  let image = null;
  let layerParams;
  let missionInfo = missionName.split('/');
  if (missionInfo[0] === 'LANDSAT') {
    function applyScaleFactors(img) {
      var opticalBands = img.select('SR_B.').multiply(0.0000275).add(-0.2);
      var thermalBands = img.select('ST_B.*').multiply(0.00341802).add(149.0);
      return img.addBands(opticalBands, null, true).addBands(thermalBands, null, true);
    }
    let bands = [mission.bands.red, mission.bands.green, mission.bands.blue];
    let visualization = {
      bands: bands,
      min: 0.0,
      max: 0.4,
    };
    image = collection.mosaic().visualize(visualization).rename(bands).clip(geometry);
    // `visualize` above already stretched the image to 8-bit RGB (0-255), so the
    // layer must not inherit the mission's own reflectance min/max on top of it —
    // that would clip everything above 0.4 to white.
    layerParams = { bands };
  } else {
    image = collection.mosaic().clip(geometry);
  }
  const layer = generateLayer(image, mission, i18n.t('forms.imageryOverlay.base'), layerParams);
  console.log('push Image', date);
  yield put(Imagery.pushImage(mission.shortname + '/' + date, date, mission.name));
  yield put(Imagery.loadLayer(layer, imageId));
};

const handleAcquireImageIntertidal = function* ({ payload: { missionName, date } }) {
  const { geometry, satellite } = yield select(getAcquisitionParameters);
  const { imageId } = yield select(getImageryIdentifiers);

  const mission = satellite.get(missionName);
  const image = mission.algorithms.acquire(date, geometry);

  const layer = generateLayer(image, mission, i18n.t('forms.imageryOverlay.base'));
  yield put(Imagery.pushImage(mission.shortname + '/' + date, date, mission.name));
  yield put(Imagery.loadLayer(layer, imageId));
};

function* fetchOneThumbnail(entry, index, satellite, geometry, thumbnailFn) {
  try {
    const mission = satellite.get(entry.name);
    const image = mission.algorithms.acquire(entry.date, geometry);
    const visParam = generateVisualizationParams(mission);
    const url = yield callback(thumbnailFn, image, geometry, visParam);
    yield put(
      Actions.insertMetadata(entry.name, entry.date, { id: entry.id ?? index, thumbnail: url, clouds: entry.content }),
    );
  } catch (entryError) {
    console.error(`Error processing entry ${entry.id}:`, entryError);
  }
}

const THUMBNAIL_WORKER_COUNT = 6;

function* thumbnailWorker(queue, satellite, geometry, thumbnailFn) {
  while (queue.length) {
    const { entry, index } = queue.shift();
    yield call(fetchOneThumbnail, entry, index, satellite, geometry, thumbnailFn);
  }
}

const handleLoadThumbnails = function* (action) {
  let { geometry, satellite, availableDates } = yield select(getAcquisitionParameters);
  if (action?.payload?.images) availableDates = action.payload.images;
  const metadata = yield select((state) => state.acquisition.metadata);
  const alreadyFetched = new Set(metadata.map((m) => `${m.date}-${m.missionName}`));
  const thumbnailFn = window.location.href.indexOf('csqueeze') === -1 ? createThumbnail : createThumbnailCSqueeze;

  const queue = availableDates
    .map((entry, index) => ({ entry, index }))
    .filter(({ entry }) => !alreadyFetched.has(`${entry.date}-${entry.name}`));

  const workerCount = Math.min(THUMBNAIL_WORKER_COUNT, queue.length);
  yield all(Array.from({ length: workerCount }, () => call(thumbnailWorker, queue, satellite, geometry, thumbnailFn)));
};

const handleLoadThumbnailsCancellable = function* (action) {
  yield race({
    task: call(handleLoadThumbnails, action),
    cancel: take(Types.CANCEL_THUMBNAILS),
  });
};

const root = function* () {
  yield all([
    concurrentHandler(Types.LOAD_AVAILABLE_IMAGES, handleLoadAvailableImages),
    bufferedHandler(Types.ACQUIRE_IMAGE, handleAcquireImage),
    bufferedHandler(Types.ACQUIRE_IMAGE_INTERTIDAL, handleAcquireImageIntertidal),
    concurrentHandler(Types.LOAD_THUMBNAILS, handleLoadThumbnailsCancellable),
  ]);
};

export default root;
